/**
 * {@link DataTable} for holidays.
 */
import * as React from 'react';
import { Calendar } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DataTableFilters } from '@/components/common/data-table/DataTableFilters';
import { DataTable } from '@/components/common/data-table';
import { Button } from '@/components/forms/Button';
import { HolidayRow } from './HolidayRow';
import { HolidayCard } from './HolidayCard';
import { HolidayFiltersContent } from './HolidayFilters';
import { DetailsViewContent } from '@/components/common/details/DetailsViewContent';
import { renderHolidayDetails } from './renderHolidayDetails';
import { DeleteModal } from '@/components/common/modals/DeleteModal';
import { useDataTable } from '@/hooks/useDataTable';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { getHolidays, deleteHoliday, exportHolidaysExcel } from '@/services/holidays.service';
import { cn } from '@/lib/utils';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import {
  DEFAULT_HOLIDAY_FILTERS,
  ALL_FILTER_GROUP_IDS,
  FILTER_GROUP_LABELS,
  type HolidayFilters,
  type FilterGroupId,
} from './holiday-configs';
import { hasHolidayActiveFilters, toHolidayListParams } from './holiday-table-utils';
import type { ViewMode } from '@/types/data-table.types';
import type { ElementWidth } from '@/types/interface-elements.types';
import type { Holiday } from '@/types/holiday.types';

/** Props for {@link HolidaysDataTable}. */
interface HolidaysDataTableProps {
  holidays?: Holiday[];
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  layoutWidth?: ElementWidth;
  expandedRowId?: string | null;
  onToggleRow?: (id: string) => void;
  onCloseDetails?: () => void;
  onEditHoliday?: (id: string) => void;
  showCreateButton?: boolean;
  onCreateHoliday?: () => void;
  enableFilters?: boolean;
  defaultFilters?: HolidayFilters;
  isSection?: boolean;
  pageActions?: { canEditRow?: boolean; canDeleteRow?: boolean; canExportCsv?: boolean };
  onFiltersChange?: (filters: HolidayFilters) => void;
  /** Optional node rendered in the DataTable toolbar left slot. */
  toolbarLeft?: React.ReactNode;
  /** Extra nodes rendered at the start of the section-actions bar (before filter/export/create). */
  toolbarExtra?: React.ReactNode;
}

/** Holidays list / grid with filters, details, delete, and Excel export. */
export const HolidaysDataTable: React.FC<HolidaysDataTableProps> = ({
  holidays: externalHolidays,
  viewMode: externalViewMode,
  onViewModeChange: externalOnViewModeChange,
  layoutWidth = 'full',
  expandedRowId,
  onToggleRow,
  onCloseDetails,
  onEditHoliday,
  showCreateButton = false,
  onCreateHoliday,
  enableFilters = true,
  defaultFilters = DEFAULT_HOLIDAY_FILTERS,
  isSection = false,
  pageActions,
  onFiltersChange,
  toolbarLeft,
  toolbarExtra,
}) => {
  const queryClient = useQueryClient();
  const showExport = pageActions?.canExportCsv === true;

  const dt = useDataTable<HolidayFilters, FilterGroupId>({
    tableName: 'holidays',
    defaultFilters,
    allFilterGroupIds: ALL_FILTER_GROUP_IDS,
    layoutWidth,
    isSection,
    externalViewMode,
    onExternalViewModeChange: externalOnViewModeChange,
    externalExpandedRowId: expandedRowId,
    onCloseDetails,
    onFiltersChange,
  });

  const { data: apiResponse, isLoading } = useQuery({
    queryKey: ['holidays', dt.currentPage, dt.itemsPerPage, dt.filters],
    queryFn: async () =>
      getHolidays(toHolidayListParams(dt.filters, dt.currentPage, dt.itemsPerPage)),
    enabled: !externalHolidays,
  });

  const data = React.useMemo(
    () => externalHolidays ?? (apiResponse?.data || []),
    [externalHolidays, apiResponse?.data],
  );
  const totalItems = apiResponse?.total ?? data.length;
  const totalPages = Math.ceil(totalItems / dt.itemsPerPage) || 1;
  const startIndex = (dt.currentPage - 1) * dt.itemsPerPage;
  const endIndex = startIndex + dt.itemsPerPage;

  const [isExporting, setIsExporting] = React.useState(false);
  const isEmptyForExport = React.useMemo(
    () => (externalHolidays ? data.length === 0 : !isLoading && totalItems === 0),
    [externalHolidays, data.length, isLoading, totalItems],
  );

  /** Excel export of the current filter set (blocked when the list is empty). */
  const handleExportExcel = React.useCallback(async () => {
    if (isEmptyForExport) {
      toast.warning(getErrorMessage('exportEmpty'));
      return;
    }
    setIsExporting(true);
    try {
      await exportHolidaysExcel();
      toast.success('Export Excel réussi !');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsExporting(false);
    }
  }, [isEmptyForExport]);

  const {
    data: expandedHolidayData,
    isLoading: isLoadingExpandedHoliday,
    isError: isExpandedHolidayError,
    error: expandedHolidayError,
  } = useQuery({
    queryKey: ['holidays', expandedRowId],
    queryFn: async () => {
      if (!expandedRowId) return null;
      const { getHoliday } = await import('@/services/holidays.service');
      return await getHoliday(expandedRowId);
    },
    enabled: !!expandedRowId,
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError: isExpandedHolidayError,
    error: expandedHolidayError,
    catalogKey: 'holidayNotFound',
    onClose: onCloseDetails,
    entityId: expandedRowId,
  });

  const hasActiveFilters = React.useMemo(() => hasHolidayActiveFilters(dt.filters), [dt.filters]);

  const deleteMutation = useMutation({
    mutationFn: deleteHoliday,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['holidays'] });
      dt.setDeleteId(null);
      toast.success('Vacances supprimées avec succès !');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const holidayToDelete = React.useMemo(
    () => data.find((h) => h.id === dt.deleteId),
    [data, dt.deleteId],
  );

  const toggleTypeFilter = React.useCallback(
    (type: string) => {
      dt.setFilters((prev) => ({
        ...prev,
        types: prev.types.includes(type)
          ? prev.types.filter((t) => t !== type)
          : [...prev.types, type],
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setDateRange = React.useCallback(
    (range: HolidayFilters['dateRange']) => {
      dt.setFilters((prev) => ({ ...prev, dateRange: range }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setCustomDateRange = React.useCallback(
    (from: Date | undefined, to: Date | undefined) => {
      dt.setFilters((prev) => ({ ...prev, customDateFrom: from, customDateTo: to }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setVacationZone = React.useCallback(
    (vacationZoneId: string) => {
      dt.setFilters((prev) => ({
        ...prev,
        vacationZoneId: vacationZoneId || undefined,
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const filtersContent = React.useMemo(
    () => (
      <HolidayFiltersContent
        filters={dt.filters}
        allowedFilterIds={dt.allowedFilterIds}
        visibleFilterIds={dt.visibleFilterIds}
        onToggleType={toggleTypeFilter}
        onSetDateRange={setDateRange}
        onSetCustomDateRange={setCustomDateRange}
        onSetVacationZone={setVacationZone}
      />
    ),
    [
      dt.filters,
      dt.allowedFilterIds,
      dt.visibleFilterIds,
      toggleTypeFilter,
      setDateRange,
      setCustomDateRange,
      setVacationZone,
    ],
  );

  const filterGroups = React.useMemo(
    () => [
      {
        id: 'filters',
        label: 'Filtres',
        type: 'custom' as const,
        customRender: () => filtersContent,
      },
    ],
    [filtersContent],
  );

  const expandedHoliday = expandedHolidayData ?? null;

  return (
    <DataTable
      data={data}
      totalItems={totalItems}
      isLoading={isLoading}
      viewMode={dt.viewMode}
      onViewModeChange={dt.handleViewModeChange}
      currentPage={dt.currentPage}
      totalPages={totalPages}
      startIndex={startIndex}
      endIndex={endIndex}
      itemLabel="vacances/fériés"
      onPageChange={dt.setCurrentPage}
      hasActiveFilters={hasActiveFilters}
      containerRef={dt.containerRef}
      expandedColSpan={8}
      toolbarLeft={toolbarLeft}
      toolbarExtra={toolbarExtra}
      toolbarActions={
        <>
          {enableFilters && (
            <Button
              preset="toolbar-filter"
              className={cn(dt.showFilters && 'section-action-button-active')}
              showBadge={hasActiveFilters}
              onClick={() => {
                dt.setShowFilters((prev) => {
                  if (prev) dt.setShowConfigure(false);
                  return !prev;
                });
              }}
            />
          )}
          {showExport && (
            <Button
              preset="toolbar-export"
              onClick={handleExportExcel}
              loading={isExporting}
              disabled={isEmptyForExport}
              tooltipText={
                isEmptyForExport && !isExporting
                  ? getErrorMessage('exportEmpty')
                  : 'Exporter la liste des périodes en Excel'
              }
            />
          )}
          {showCreateButton && onCreateHoliday && (
            <Button
              preset="toolbar-create"
              tooltipText="Créer une période"
              onClick={onCreateHoliday}
            />
          )}
        </>
      }
      filtersPanel={
        enableFilters ? (
          <DataTableFilters
            show={dt.showFilters}
            hasActiveFilters={hasActiveFilters}
            filterGroups={filterGroups}
            onClearFilters={dt.clearFilters}
            resultCount={totalItems}
            resultLabel="période(s)"
            onToggleConfigure={() => dt.setShowConfigure((prev) => !prev)}
            showConfigure={dt.showConfigure}
            configureContent={
              <div className="flex flex-wrap gap-3">
                {ALL_FILTER_GROUP_IDS.filter((id) => dt.allowedFilterIds.includes(id)).map((id) => {
                  const isOn = dt.filterVisibility[id] ?? true;
                  return (
                    <Button
                      key={id}
                      variant="custom"
                      size="sm"
                      onClick={() => dt.handleToggleFilterVisibility(id)}
                      disabled={dt.isSavingFilterVisibility}
                      className={cn(
                        'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
                        dt.isSavingFilterVisibility && 'opacity-60 cursor-not-allowed',
                        isOn
                          ? 'bg-[var(--color-2-nuance-1)] border-[var(--color-2-nuance-1)] text-white'
                          : 'bg-transparent border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)] text-muted-foreground hover:border-[var(--color-1-nuance-2)]',
                      )}
                    >
                      {FILTER_GROUP_LABELS[id]}
                    </Button>
                  );
                })}
              </div>
            }
          />
        ) : undefined
      }
      renderTableHead={() => (
        <tr>
          <th>Nom</th>
          <th>Type</th>
          <th>Zone</th>
          <th>Date de début</th>
          <th>Date de fin</th>
          <th>Options</th>
          <th>Description</th>
          <th className="w-20 text-center">Actions</th>
        </tr>
      )}
      renderTableRow={(holiday) => (
        <HolidayRow
          holiday={holiday}
          onView={onToggleRow ? () => onToggleRow(holiday.id) : undefined}
          onEdit={onEditHoliday}
          onDelete={(id) => dt.setDeleteId(id)}
          pageActions={pageActions}
        />
      )}
      expandedRowId={dt.expandedRowId}
      renderExpandedRow={(holiday) => (
        <DetailsViewContent>{renderHolidayDetails(holiday)}</DetailsViewContent>
      )}
      expandedRowRef={dt.expandedRowRef}
      expandedCollapseRef={dt.expandedCollapseRef}
      renderCard={(holiday) => (
        <HolidayCard
          holiday={holiday}
          onView={onToggleRow ? () => onToggleRow(holiday.id) : undefined}
          onEdit={onEditHoliday}
          onDelete={(id) => dt.setDeleteId(id)}
          pageActions={pageActions}
        />
      )}
      emptyIcon={Calendar}
      emptyText="Aucune période trouvée"
      title="Vacances"
      sidebarMenuId="menu-holidays"
      isSection={isSection}
      expandedItem={expandedHoliday}
      isExpandedDetailLoading={isLoadingExpandedHoliday}
      onCloseDetails={onCloseDetails}
      renderDetailsContent={(holiday) => (
        <DetailsViewContent>{renderHolidayDetails(holiday)}</DetailsViewContent>
      )}
      modals={
        <DeleteModal
          open={!!dt.deleteId}
          onClose={() => dt.setDeleteId(null)}
          onConfirm={() => {
            if (dt.deleteId) deleteMutation.mutate(dt.deleteId);
          }}
          itemName={holidayToDelete?.name}
          isLoading={deleteMutation.isPending}
        />
      }
    />
  );
};
